import { useCallback, useSyncExternalStore } from 'react';

/**
 * Routing, in about sixty lines.
 *
 * Four destinations and one query parameter do not need a router library, and
 * adding one would mean a second place where "which guild am I looking at" is
 * modelled. `history.pushState` plus `popstate` is the whole mechanism, so the
 * back button, a bookmark, and a copied URL all work without anything being
 * wired up for them specifically.
 *
 * The browser's history is an external store, so it is read through
 * `useSyncExternalStore` rather than mirrored into `useState` from an effect.
 * The difference is not stylistic: a `useState` mirror is a second copy of the
 * URL that can be a render behind it, and under concurrent rendering that shows
 * up as a page briefly drawn for the previous route.
 *
 * The selected guild lives in the query string on purpose. It is view state, it
 * belongs in a shareable URL, and it is explicitly not a credential — which is
 * why it goes here rather than into any kind of browser storage.
 */

export const ROUTE_PATHS = [
  '/',
  '/memory',
  '/query-lab',
  '/operations',
  '/server-settings',
  '/settings',
] as const;
export type RoutePath = (typeof ROUTE_PATHS)[number];

export const ROUTE_TITLES: Readonly<Record<RoutePath, string>> = {
  '/': 'Overview',
  '/memory': 'Memory health',
  '/query-lab': 'Query Lab',
  '/operations': 'Operations',
  '/server-settings': 'Server settings',
  '/settings': 'Bot settings',
};

export function isRoutePath(value: string): value is RoutePath {
  return (ROUTE_PATHS as readonly string[]).includes(value);
}

export interface Location {
  path: RoutePath;
  guildId: string | null;
}

/**
 * The store half of `useSyncExternalStore`.
 *
 * `pushState` and `replaceState` do not fire `popstate`, so navigations made by
 * this module notify subscribers explicitly. Without that, only the back button
 * would re-render.
 */
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('popstate', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * `useSyncExternalStore` compares snapshots by identity, so this must return the
 * same string for the same URL rather than a fresh object. The `Location` is
 * parsed from it once per render.
 */
function getSnapshot(): string {
  return window.location.href;
}

function parse(href: string): Location {
  const url = new URL(href);
  const path = url.pathname.replace(/\/+$/, '') || '/';
  return {
    path: isRoutePath(path) ? path : '/',
    guildId: url.searchParams.get('guild'),
  };
}

export function useLocationState(): {
  location: Location;
  navigate: (path: RoutePath) => void;
  selectGuild: (guildId: string | null) => void;
} {
  const href = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const location = parse(href);

  const navigate = useCallback((path: RoutePath) => {
    const url = new URL(window.location.href);
    url.pathname = path;
    window.history.pushState({}, '', url);
    notify();
  }, []);

  // Changing servers replaces rather than pushes: a guild switch is a change of
  // subject, not a step in a trail, and stacking twenty of them makes the back
  // button useless for leaving the page.
  const selectGuild = useCallback((guildId: string | null) => {
    const url = new URL(window.location.href);
    if (guildId === null) url.searchParams.delete('guild');
    else url.searchParams.set('guild', guildId);
    window.history.replaceState({}, '', url);
    notify();
  }, []);

  return { location, navigate, selectGuild };
}
